// skipcq: JS-W1028, JS-0128
import React, { useCallback, useState } from 'react';
// libs
import { useNavigate } from 'react-router-dom';
// Material UI
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
// config
import { XS_CARD_WIDTH, MD_CARD_WIDTH } from '../../utils/constants';
// components
import Socials from '../../components/socials/Socials';
import Amenities from '../../components/amenities/Amenities';
// Icons
import PublicIcon from '@mui/icons-material/Public';
import VerifiedIcon from '@mui/icons-material/Verified';
import sanitizedUrl from '../../utils/sanitizeURL';

// Renders website link + socials row and amenities with minimal nesting
function RightMeta({ url, website, socials, amenities }) {
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1
        }}>
        {website ? (
          <Link href={url} target="_blank" rel="noopener noreferrer">
            <PublicIcon />
          </Link>
        ) : null}
        <Box sx={{
          flex: 1
        }}>
          <Socials socials={socials} />
        </Box>
      </Box>
      <Amenities
        amenities={amenities}
        settings={{ showLabels: false, vertical: false }}
      />
    </Box>
  );
}

function BreweryIdentityColumn({
  name,
  address1,
  city,
  state,
  zip,
  phone,
  isOob,
  oobDate,
  hasBreweryAdmin,
}) {
  return (
    <Grid item xs={12} md={5}>
      {isOob && (
        <Typography color="error">Out of business {oobDate}</Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}>
        <Typography variant="h5" component="span">
          {name}
        </Typography>
        {hasBreweryAdmin === true ? (
          <Tooltip title="Claim approved">
            <VerifiedIcon color="primary" aria-label="Claim approved" fontSize="small" />
          </Tooltip>
        ) : null}
      </Box>
      <Typography variant="body2">{address1}</Typography>
      <Typography variant="body2">
        {city} {state} {zip}
      </Typography>
      <Typography variant="body2">{phone}</Typography>
    </Grid>
  );
}

function BreweryCardActionsRow({
  handleSelectForMerge,
  selectedForMergeProp,
  selectedForMerge,
  handleViewClick,
}) {
  return (
    <Grid
      item
      xs={12}
      md={12}
      container
      sx={{
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 1
      }}>
      <FormControlLabel
        control={
          <Checkbox
            color="primary"
            checked={selectedForMergeProp ?? selectedForMerge}
            onChange={handleSelectForMerge}
            inputProps={{ 'aria-label': 'Select for Merge' }}
          />
        }
        label="Select for Merge"
      />
      <Button variant="contained" onClick={handleViewClick}>
        View
      </Button>
    </Grid>
  );
}

/** Splits grid markup so BreweryCard JSX stays shallow (DeepSource JS-0415). */
function BreweryCardInnerGrid(props) {
  const {
    name,
    logoUrl,
    address1,
    city,
    state,
    zip,
    phone,
    isOob,
    oobDate,
    amenities,
    website,
    url,
    socials,
    hasBreweryAdmin,
    handleSelectForMerge,
    selectedForMergeProp,
    selectedForMerge,
    handleViewClick,
  } = props;

  return (
    <Grid container spacing={1} sx={{
      alignItems: "center"
    }}>
      <BreweryIdentityColumn
        name={name}
        address1={address1}
        city={city}
        state={state}
        zip={zip}
        phone={phone}
        isOob={isOob}
        oobDate={oobDate}
        hasBreweryAdmin={hasBreweryAdmin}
      />
      <Grid item xs={4} md={3} container sx={{
        justifyContent: "center"
      }}>
        <img src={logoUrl} alt={name} width="100" />
      </Grid>
      <Grid item xs={8} md={4}>
        <RightMeta
          url={url}
          website={website}
          socials={socials}
          amenities={amenities}
        />
      </Grid>
      <BreweryCardActionsRow
        handleSelectForMerge={handleSelectForMerge}
        selectedForMergeProp={selectedForMergeProp}
        selectedForMerge={selectedForMerge}
        handleViewClick={handleViewClick}
      />
    </Grid>
  );
}

function BreweryCard({
  brewery,
  id,
  selectedForMerge: selectedForMergeProp,
  onSelectForMergeChange,
}) {
  const navigate = useNavigate();

  // Track whether this brewery is selected for merge
  const [selectedForMerge, setSelectedForMerge] = useState(false);

  const handleViewClick = useCallback(() => {
    try {
      sessionStorage.setItem('breweries.anchorId', String(id));
    } catch {
      // ignore sessionStorage errors
    }
    navigate(`/brewery/${id}`);
  }, [navigate, id]);

  const handleSelectForMerge = useCallback(
    (event) => {
      const checked = event.target.checked;
      if (typeof onSelectForMergeChange === 'function') {
        onSelectForMergeChange(id, checked);
      }
      if (selectedForMergeProp === undefined) {
        setSelectedForMerge(checked);
      }
    },
    [onSelectForMergeChange, id, selectedForMergeProp],
  );

  if (brewery === null) {
    return null;
  }

  const {
    name,
    address1,
    city,
    state,
    zip,
    website,
    phone,
    isOob,
    oobDate,
    amenities = {},
    hasBreweryAdmin,
  } = brewery;

  // Ensure socials is always an array
  const socials = Array.isArray(brewery.socials) ? brewery.socials : [];

  let { logoUrl } = brewery;

  if (!logoUrl) {
    logoUrl =
      'https://firebasestorage.googleapis.com/v0/b/id-tap-that-backend.appspot.com/o/app_icon50.png?alt=media&token=2c784a42-a970-42e2-98ed-d3c8645ecd6f';
  }

  const url = sanitizedUrl(website);

  return (
    <Card
      id={`brewery-card-${id}`}
      variant="outlined"
      sx={{
        minWidth: {
          xs: XS_CARD_WIDTH,
          md: MD_CARD_WIDTH,
        },
        width: {
          xs: XS_CARD_WIDTH,
          md: MD_CARD_WIDTH,
        },
        margin: '4px',
      }}
    >
      <CardContent>
        <BreweryCardInnerGrid
          name={name}
          logoUrl={logoUrl}
          address1={address1}
          city={city}
          state={state}
          zip={zip}
          phone={phone}
          isOob={isOob}
          oobDate={oobDate}
          amenities={amenities}
          website={website}
          url={url}
          socials={socials}
          hasBreweryAdmin={hasBreweryAdmin}
          handleSelectForMerge={handleSelectForMerge}
          selectedForMergeProp={selectedForMergeProp}
          selectedForMerge={selectedForMerge}
          handleViewClick={handleViewClick}
        />
      </CardContent>
    </Card>
  );
}

export default BreweryCard;
